import React, { useState } from "react";
import { CalendarClock, ChevronDown, ChevronUp, FileOutput, Pencil, Loader2 } from "lucide-react";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

const chf = (v) => Number(v || 0).toFixed(2);

/** Nächste Rate / Restbetrag als Zahlungsauftrag pain.001 herunterladen. */
export function InstallmentPainButton({ bill, installmentNo = null, label }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await apiClient.get(`/bills/${bill._id}/installment-pain001`, { params: installmentNo ? { installment_no: installmentNo } : {}, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/)?.[1] || "pain001.xml";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success("Zahlungsauftrag (pain.001) erstellt – im E-Banking hochladen");
    } catch (e) {
      let msg = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ } }
      toast.error(msg || "Zahlungsauftrag fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} className="inline-flex items-center gap-1 rounded border border-indigo-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50" data-testid={`bill-pain001-${bill._id}${installmentNo ? `-${installmentNo}` : ""}`} title="Zahlungsauftrag pain.001 für das E-Banking erzeugen">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileOutput className="h-3 w-3" />} {label || "Rate als pain.001"}
    </button>
  );
}

/** Zahlungsplan an einer Eingangsrechnung: bezahlt / offen / nächste Rate, aufklappbare Ratenliste, Aktionen. */
export function BillPaymentPlan({ bill, onEdit }) {
  const [open, setOpen] = useState(false);
  const plan = bill.payment_plan;
  const total = Number(bill.amount_total || bill.total || 0);
  const paid = Number(bill.amount_paid || 0);
  const rest = Math.max(0, total - paid);
  const payable = bill.status !== "paid";
  if (!plan?.installments?.length && !paid) {
    return payable && onEdit ? (
      <button type="button" onClick={() => onEdit(bill)} className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-indigo-700 normal-case" data-testid={`bill-plan-create-${bill._id}`} title="Ratenzahlung vereinbart? Raten mit Datum und Betrag eintragen">
        <CalendarClock className="h-3 w-3" /> Ratenplan anlegen
      </button>
    ) : null;
  }
  const inst = plan?.installments || [];
  const next = inst.find((i) => !i.paid);
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  const overdue = next && next.due_date < new Date().toISOString().slice(0, 10);
  return (
    <div className="mt-1 text-[11px] normal-case" data-testid={`bill-plan-${bill._id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-800 px-1.5 py-0.5 font-medium">
          <CalendarClock className="h-3 w-3" /> {plan?.kind === "vat" ? "MWST-Zahlungsplan" : plan ? "Zahlungsplan" : "Teilzahlung"}{inst.length ? ` · ${inst.filter((i) => i.paid).length}/${inst.length} Raten` : ""}
        </span>
        <span className="text-emerald-700">bezahlt {chf(paid)}</span>
        <span className={rest > 0 ? "text-rose-700 font-medium" : "text-slate-500"}>offen {chf(rest)}</span>
        {next && (
          <span className={overdue ? "text-rose-700 font-medium" : "text-slate-600"} data-testid={`bill-plan-next-${bill._id}`}>
            · nächste Rate {formatDate(next.due_date)} CHF {chf(next.amount)}{overdue ? " (überfällig)" : ""}
          </span>
        )}
        {inst.length > 0 && (
          <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center text-slate-500 hover:text-slate-800" data-testid={`bill-plan-toggle-${bill._id}`}>
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}
      </div>
      <div className="mt-1 h-1.5 w-48 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>
      {payable && rest > 0 && (
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          <InstallmentPainButton bill={bill} label={next ? `Rate ${next.no} als pain.001` : "Rest als pain.001"} />
          {onEdit && (
            <button type="button" onClick={() => onEdit(bill)} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] text-slate-700 hover:bg-slate-50" data-testid={`bill-plan-edit-${bill._id}`}>
              <Pencil className="h-3 w-3" /> {plan ? "Raten ändern" : "Ratenplan anlegen"}
            </button>
          )}
        </div>
      )}
      {open && (
        <ul className="mt-1.5 space-y-0.5" data-testid={`bill-plan-list-${bill._id}`}>
          {inst.map((i) => (
            <li key={i.no} className={`flex items-center gap-3 ${i.paid ? "text-emerald-700" : "text-slate-700"}`}>
              <span className="w-14">Rate {i.no}</span>
              <span className="w-24">{formatDate(i.due_date)}</span>
              <span className="w-24 text-right font-mono">{chf(i.amount)}</span>
              <span>{i.paid ? "✓ bezahlt" : i.paid_amount > 0 ? `teilweise (${chf(i.paid_amount)})` : "offen"}</span>
              {!i.paid && payable && <InstallmentPainButton bill={bill} installmentNo={i.no} label="pain.001" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
